import * as Sentry from '@sentry/nuxt'
import type { Event as SentryEvent } from '@sentry/core'

const config = useRuntimeConfig()

// Skip Sentry initialization if SENTRY_DISABLED is set to any value
if (config.public.sentry?.disabled) {
  // Sentry is disabled via SENTRY_DISABLED environment variable
} else if (
  config.public.sentry?.dsn &&
  (process.env.NODE_ENV === 'production' ||
    process.env.NODE_ENV === 'development')
) {
  Sentry.init({
    debug: config.public.sentry?.debug || false,
    dsn: config.public.sentry.dsn,
    tunnel: '/api/sentry-tunnel',
    environment:
      config.public.sentry.config?.environment ||
      process.env.NODE_ENV ||
      'development',
    release: config.public.alfredVersion?.replace('-', '.') || 'noversion',
    sendDefaultPii: true, // Enable IP address collection
    tracesSampleRate: 0, // DISABLED - only error tracking, no performance tracing
    integrations: function (integrations) {
      // integrations will be all default integrations

      const toAdd = [
        // REMOVED: zodErrorsIntegration - never seen in error reports
        // REMOVED: reportingObserverIntegration - adds spam, ends up ignored
        // REMOVED: piniaIntegration - stateTransformer causes 731ms GC pressure
        // REMOVED: moduleMetadataIntegration - info already in source code
        // DISABLED: tracesSampleRate=0 - no performance tracing, errors only
        Sentry.vueIntegration({
          // Basic Vue error handling only - no component tracking
        }),
        // REMOVED: piniaIntegration - stateTransformer causes 731ms GC pressure
        // Additional integrations for better error context:
        Sentry.extraErrorDataIntegration({
          // Captures all non-native error properties as extra data
          depth: 5, // How deep to traverse the error object
        }),
        Sentry.httpClientIntegration({
          // Captures failed HTTP requests with request/response info
          failedRequestStatusCodes: [[500, 599]], // Capture 5xx errors
          failedRequestTargets: [/.*/], // Capture all failed requests
        }),
        Sentry.contextLinesIntegration({
          // Adds source code context to stack traces (client-side)
          frameContextLines: 7, // Number of lines before/after the error line
        }),
        // REMOVED: moduleMetadataIntegration - info already in source code
        // feedbackIntegration lazy-loaded on demand via loadSentryFeedback()
        // Saves ~37KB from initial bundle — only loaded on AI search + error pages
      ]
      integrations.push(...toAdd)

      // REMOVED: BrowserApiErrors — wraps setTimeout/addEventListener/etc with
      // try-catch for better stack traces, but adds ~1% CPU per interaction via
      // sentryWrapped overhead. With tracesSampleRate=0 the metadata it collects
      // isn't used. Unhandled errors are still captured by globalHandlersIntegration
      // and Vue errors by vueIntegration above.
      return integrations.filter(function (integration) {
        return (
          integration.name !== 'Dedupe' &&
          integration.name !== 'BrowserApiErrors'
        )
      })
    },
    normalizeDepth: 10,
    ignoreErrors: [
      // Your existing ignore patterns
      '403',
      /GSI_LOGGER/,
      /cookieyes/,
      /google_tag_manager is not defined/,
      /Unload event listeners are deprecated/,
      /Attribution Reporting is deprecated/,
      /Topics API is deprecated/,
      'AbortError',
      'The operation was aborted',
      'The user aborted a request',
      /operation.*aborted/i,
      /request.*aborted/i,
      /fetch.*aborted/i,
      /user.*aborted.*request/i,
      'Loading chunk',
      'Importing a module script failed',
      'Failed to fetch dynamically imported module',
      /ChunkLoadError/,
      /Loading CSS chunk/,
      /Failed to import dynamically/,
      /Blocked a frame with origin/,
      /The operation is insecure/,
      'Script loading timed out',
    ],
    beforeSend(event: SentryEvent) {
      // Allow programmatic suppression of Sentry logging
      if (typeof window !== 'undefined' && window.preventSentryLogging) {
        return null
      }

      /* eslint-disable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Sentry event structure */
      // Filter cookieyes errors by stack frame (ignoreErrors only matches messages, not stack frames)
      const hasCookieyesStack =
        event.exception?.values?.[0]?.stacktrace?.frames?.some((frame: any) =>
          frame.filename?.includes('cookieyes'),
        )
      if (hasCookieyesStack) {
        return null
      }

      // Enhance non-Error promise rejections with additional context for debugging
      const message: string = event.exception?.values?.[0]?.value || ''
      if (message.includes('Non-Error promise rejection')) {
        event.extra = event.extra || {}
        event.extra.rejectionAnalysis = {
          message: 'Promise rejected with non-Error value',
          recommendation: 'Use reject(new Error(...)) instead of reject()',
          url: event.request?.url || window?.location?.href,
        }
      }
      /* eslint-enable @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access */

      return event
    },
  })
}
